import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { campuses } from '../data/campusesData';
import './Gallery.css';

// Machohalli's facility list mixes school photos with PU/Degree College photos.
// Split those out into their own campus-wise category to match how the
// campuses are actually presented (PU & Degree College is its own listing).
const isCollegeFacility = (facility) =>
  /college/i.test(facility.label) || /college/i.test(facility.src);

const buildGalleryImages = () => {
  const images = [];

  campuses.forEach((campus) => {
    images.push({
      id: `${campus.id}-main`,
      url: campus.image,
      title: campus.name,
      category: campus.id,
    });

    (campus.facilities || []).forEach((facility, index) => {
      const category =
        campus.id === 'macholli' && isCollegeFacility(facility)
          ? 'pu-degree'
          : campus.id;

      images.push({
        id: `${campus.id}-${index}`,
        url: facility.src,
        title: facility.label,
        category,
      });
    });
  });

  return images;
};

const Gallery = () => {
  const images = useMemo(buildGalleryImages, []);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'rajaji-nagar', label: 'Rajajinagar' },
    { id: 'bashveshwarnagar', label: 'Basaveshwaranagar' },
    { id: 'macholli', label: 'Machohalli' },
    { id: 'naavruja', label: 'Nagavruja' },
    { id: 'pu-degree', label: 'PU and Degree College' },
  ];

  const filteredImages =
    activeCategory === 'all'
      ? images
      : images.filter((img) => img.category === activeCategory);

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <div className="gallery-page">
      <section className="page-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Photo Gallery</h1>
            <p>Glimpses of life at Sri Vani Education, campus by campus</p>
          </motion.div>
        </div>
      </section>

      <section className="gallery-section section">
        <div className="container">
          {/* Filter Buttons */}
          <div className="gallery-filters">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="gallery-grid">
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                className="gallery-item"
                layout="position"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.4, delay: index * 0.03 }}
                onClick={() => openLightbox(image)}
              >
                <img src={image.url} alt={image.title} />
                <div className="gallery-overlay">
                  <h3>{image.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <motion.div
          className="lightbox"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeLightbox}
        >
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeLightbox}>×</button>
            <img src={selectedImage.url} alt={selectedImage.title} />
            <h3>{selectedImage.title}</h3>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Gallery;
